/**
 * IsoVariant - Variante ISO 8601 para Date
 *
 * Representa data como string ISO 8601.
 *
 * @example
 * "2024-12-05T19:30:00.000Z"
 * "2024-12-05T19:30:00.000-03:00"
 * "2024-12-05"
 */

import { BaseVariant } from "../../../contracts";
import type { ValidationResult, FormatOptions } from "../../../types";
import type { DateIso, DateTimestamp, DateFormatOptions } from "../types";

// Regex para validar ISO 8601 (formatos comuns)
const ISO_FULL_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})?$/;
const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

export class IsoVariant extends BaseVariant<DateIso, DateTimestamp> {
  readonly name = "iso";
  readonly type = "string" as const;

  // ===========================================================================
  // CONVERT
  // ===========================================================================

  toBase(value: DateIso): DateTimestamp {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid ISO date: ${value}`);
    }
    return date.getTime();
  }

  fromBase(base: DateTimestamp): DateIso {
    const date = new Date(base);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid timestamp: ${base}`);
    }
    return date.toISOString();
  }

  // ===========================================================================
  // CAST
  // ===========================================================================

  cast(input: unknown): DateIso | null {
    // Já é string
    if (typeof input === "string") {
      const trimmed = input.trim();

      // Tenta parsear
      const date = new Date(trimmed);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }

      return null;
    }

    // Número (timestamp)
    if (typeof input === "number") {
      if (!Number.isFinite(input)) return null;
      const date = new Date(input);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
      return null;
    }

    // Date object
    if (input instanceof Date) {
      if (isNaN(input.getTime())) return null;
      return input.toISOString();
    }

    return null;
  }

  // ===========================================================================
  // VALIDATE
  // ===========================================================================

  validate(value: DateIso): ValidationResult {
    const errors: string[] = [];

    if (typeof value !== "string") {
      errors.push("ISO date must be a string");
      return { valid: false, errors };
    }

    const trimmed = value.trim();

    // Verifica se é um formato ISO válido
    if (
      !ISO_DATE_TIME_REGEX.test(trimmed) &&
      !ISO_DATE_ONLY_REGEX.test(trimmed)
    ) {
      errors.push("Invalid ISO 8601 format");
      return { valid: false, errors };
    }

    // Verifica se a data é parseável
    const date = new Date(trimmed);
    if (isNaN(date.getTime())) {
      errors.push("Invalid date value");
      return { valid: false, errors };
    }

    return { valid: true, errors: [] };
  }

  // ===========================================================================
  // FORMAT
  // ===========================================================================

  format(value: DateIso, options?: FormatOptions): string {
    const opts = options as DateFormatOptions | undefined;
    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    // Se não especificar opções, retorna o ISO string
    if (
      !opts ||
      (!opts.dateStyle && !opts.timeStyle && !opts.dateOnly && !opts.timeOnly)
    ) {
      return value;
    }

    const locale = opts.locale || "en-US";
    const formatOptions: Intl.DateTimeFormatOptions = {};

    if (opts.timeZone) {
      formatOptions.timeZone = opts.timeZone;
    }

    if (opts.dateOnly) {
      formatOptions.dateStyle = opts.dateStyle || "medium";
    } else if (opts.timeOnly) {
      formatOptions.timeStyle = opts.timeStyle || "medium";
    } else {
      if (opts.dateStyle) formatOptions.dateStyle = opts.dateStyle;
      if (opts.timeStyle) formatOptions.timeStyle = opts.timeStyle;
    }

    return new Intl.DateTimeFormat(locale, formatOptions).format(date);
  }
}
